import React, { useState } from "react";
import { Modal as MuiModal } from "@mui/material";
import { Button } from "subframe/components/Button";
import { RadioGroup } from "subframe/components/RadioGroup";
import MuiAlert from "@mui/material/Alert";
import { Snackbar } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "hooks/useDebounce";
import { Autocomplete, TextField as MuiTextField } from "@mui/material";
import { isEmpty } from "utils/array";

import { getActiveEmployeesEmail } from "services/retention/employees";

import {
  createCaregiverRolePolicy,
  updateCaregiverRolePolicy,
} from "services/retention/employees";
import {
  ACCESSIBILITY_TYPE,
  ACCESSIBILITY_TYPE_ENUM,
  ROLE_TYPE,
  ROLE_TYPE_ENUM,
} from "modules/Retention/constants/constants";
import UserPolicyMultiSelectDropdown from "./UserPolicyMultiSelectDropdown";
import endpoints from "constants/retention/endpoints";

export default function UserPolicyModal({
  open,
  setOpenAddModal,
  data = null,
  updateData = () => {},
  userPolicyOptions,
  onSuccess,
}) {
  const [accessibilityType, setAccessibilityType] = useState(() =>
    data && data.type !== "-" ? data.type : ACCESSIBILITY_TYPE_ENUM.REGION
  );
  const [email, setEmail] = useState(() => data?.email ?? "");
  const [roleType, setRoleType] = useState(() => 
    data ? data.role : ROLE_TYPE_ENUM.CAREGIVER
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedValues, setSelectedValues] = React.useState([]);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);

  const debouncedEmail = useDebounce(email, 500);

  const { data: caregiverRoles, isLoading: isFetchingCaregiverRoles, error } = useQuery({
    queryKey: [endpoints.activeEmployeeEmails, debouncedEmail],
    queryFn: () => getActiveEmployeesEmail({ searchEmail: debouncedEmail}),
  });

  const handleClose = () => {
    setOpenAddModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate manager role requirements
    if (roleType === ROLE_TYPE_ENUM.CAREGIVER) {
      if (!accessibilityType) {
        setErrorMessage("Please select an accessibility type");
        setIsErrorMessageShown(true);
        return;
      }
      if (isEmpty(selectedValues)) {
        setErrorMessage(`Please select at least one ${accessibilityType.replace("_", "")}`);
        setIsErrorMessageShown(true);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      let policy;
      
      if (roleType === ROLE_TYPE_ENUM.ADMIN) {
        // For admin role, save the specific format
        policy = [{
          method: "GET",
          type: "region",
          value: "",
          role: "admin"
        }];
      } else {
        // For other roles, use the existing logic
        const accessScope = selectedValues.map((v) => v.label);
        const type = accessibilityType;
        
        policy = [{
          method: "GET",
          type: type,
          value: accessScope,
          role: roleType
        }];
      }

      if (data) {
        await updateCaregiverRolePolicy({
          email: data.email,
          role_type: roleType,
          policy: policy,
        });
        setSuccessMessage("Caregiver role policy updated successfully");
        setIsSuccessMessageShown(true);
      } else {
        await createCaregiverRolePolicy({
          email: email.trim(),
          role_type: roleType,
          policy: policy,
        });
        setSuccessMessage("Caregiver role created successfully");
        setIsSuccessMessageShown(true);
      }
      updateData({ ...data, type: policy[0].type, value: policy[0].value, role: policy[0].role });
      onSuccess();
      setOpenAddModal(false);
    } catch (error) {
      console.error("Failed to create caregiver role:", error);
      setErrorMessage(
        error?.message || "Failed to create caregiver role"
      );
      setIsErrorMessageShown(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  return (
    <>
      <MuiModal
        open={open}
        onClose={handleClose}
        aria-labelledby="user-policy-edit-modal"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-[400px]">
          <h2
            id="user-policy-edit-modal"
            className="text-lg font-semibold mb-4"
          >
            {`${data ? "Edit" : "Add"} accessibility policy`}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!data && (
              <Autocomplete
                disablePortal
                size="small"
                sx={{borderRadius: '8px'}}
                id="email-autocomplete"
                placeholder="Email"
                options={caregiverRoles?.result?.employees || []}
                loading={isFetchingCaregiverRoles}
                value={email}
                onChange={(event, newValue) => {
                  setEmail(newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  setEmail(newInputValue);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    required
                    placeholder="Email"
                    InputProps={{
                      ...params.InputProps,
                      startAdornmentAdornment: (
                        <>
                          {isFetchingCaregiverRoles ? (
                            <div className="mr-2">Loading...</div>
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <div className="flex items-center">
                      <span>{option}</span>
                    </div>
                  </li>
                )}
              />
            )}
            <RadioGroup
              label="Role Type"
              helpText=""
              error={false}
              horizontal={true}
              value={roleType}
              onValueChange={(value) => {
                setRoleType(value);
                if (value === ROLE_TYPE_ENUM.ADMIN) {
                  setSelectedValues([]);
                }
              }}
            >
              {ROLE_TYPE.map((type) => (
                <RadioGroup.Option key={type.value} label={type.label} value={type.value} />
              ))}
            </RadioGroup>
            {roleType !== ROLE_TYPE_ENUM.ADMIN && (
              <>
                <div>
                  <RadioGroup
                    label="Accessibility Type"
                    helpText=""
                    error={false}
                    horizontal={true}
                    value={accessibilityType}
                    onValueChange={(value) => {
                      setAccessibilityType(value);
                    }}
                  >
                    {ACCESSIBILITY_TYPE.map((type) => (
                      <RadioGroup.Option
                        label={type.label}
                        value={type.value}
                        key={type.value}
                      />
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <UserPolicyMultiSelectDropdown
                    userPolicyOptions={userPolicyOptions}
                    initialData={data}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    accessibilityType={accessibilityType}
                  />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="brand-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                loading={isSubmitting}
                type="submit"
              >
                {data ? "Update Policy" : "Add Policy"}
              </Button>
            </div>
          </form>
        </div>
      </MuiModal>
      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </>
  );
}
